import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog_v3";
import { Button } from "../../../components/ui/button";
import { ScrollArea } from "../../../components/ui/scroll-area_v3";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, FileText, Download, Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "lib/utils";
import { PDFViewer } from "./pdf-viewer";
import { useUserStore } from "../../../context/useUserStore";
import { useThemeStore } from "../../../context/useThemeStore";
import SpecificAPI from "services/SpecificApi";

function DocumentLoaderFallback() {
  return (
    <div className="flex flex-col items-center justify-center gap-6 py-20">
      <div className="relative">
        <div className="absolute inset-0 size-20 animate-ping rounded-full bg-primary/20" />
        <div className="absolute inset-2 size-16 animate-pulse rounded-full bg-primary/30" />
        <div className="relative size-20">
          <svg className="size-20 animate-spin" viewBox="0 0 80 80">
            <circle className="stroke-muted" cx="40" cy="40" r="32" fill="none" strokeWidth="4" />
            <circle
              className="stroke-primary"
              cx="40"
              cy="40"
              r="32"
              fill="none"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="200"
              strokeDashoffset="150"
            />
          </svg>
          <FileText className="absolute left-1/2 top-1/2 size-8 -translate-x-1/2 -translate-y-1/2 text-primary" />
        </div>
      </div>
      <div className="flex flex-col items-center gap-2">
        <p className="text-lg font-medium text-foreground">Loading Document</p>
        <p className="text-sm text-muted-foreground">Please wait while we fetch your document...</p>
      </div>
      <div className="flex gap-1.5">
        <div className="size-2 animate-bounce rounded-full bg-primary [animation-delay:-0.3s]" />
        <div className="size-2 animate-bounce rounded-full bg-primary [animation-delay:-0.15s]" />
        <div className="size-2 animate-bounce rounded-full bg-primary" />
      </div>
    </div>
  );
}

export function DocumentViewerModal({ open, onOpenChange, reference }) {
  const { userData } = useUserStore();
  const { theme } = useThemeStore();
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);

  const isPdf = reference?.title.toLowerCase().endsWith(".pdf");
  const isDoc = reference?.title.toLowerCase().endsWith(".doc") || reference?.title.toLowerCase().endsWith(".docx");

  // Reset state when reference changes
  useEffect(() => {
    if (reference) {
      setIsLoading(true);
      setError(null);
      setCurrentPage(reference.page_number || 1);
      setScale(1);
    }
  }, [reference]);

  useEffect(() => {
    if (!reference?.resource_id) return;

    let isMounted = true;

    const fetchUrl = async () => {
      try {
        setIsLoading(true);
        const url = await getResourceUrl(reference.resource_id);

        if (isMounted) {
          setDocumentUrl(url);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load document URL");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchUrl();

    return () => {
      isMounted = false;
    };
  }, [reference?.resource_id]);

  useEffect(() => {
    if (!open) {
      setDocumentUrl(null);
    }
  }, [open]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }) => {
      setNumPages(numPages);
      setIsLoading(false);
      if (reference?.page_number) {
        setCurrentPage(reference.page_number);
      }
    },
    [reference?.page_number],
  );

  const onDocumentLoadError = useCallback((error) => {
    console.error("Error loading document:", error);
    setError("Failed to load document. Please try again.");
    setIsLoading(false);
  }, []);

  const goToPage = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const nextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(prev + 1, numPages));
  }, [numPages]);

  const prevPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  }, []);

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 3));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  // const getResourceUrl = (resourceId) => {
  //   return `https://pdfobject.com/pdf/sample.pdf`;
  // };

  const getResourceUrl = async (resource_id) => {
    try {
      const result = await SpecificAPI.getResourceURL(resource_id, userData?.selected_school_id);

      const url = result?.["signed-url"];

      return url;
    } catch (err) {
      console.error("Failed to fetch signed URL", err);
    }
  };

  const handleDownload = useCallback(() => {
    if (reference) {
      const url = documentUrl;
      window.open(url, "_blank");
    }
  }, [reference]);

  if (!reference) return null;

  const select_bg = theme === "dark" ? "bg-[var(--card-bg)] text-white" : "bg-white text-foreground";
  const select_bg2 = theme === "dark" ? " text-white bg-red" : "text-foreground";

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className={select_bg}>
      <DialogContent
        // className={cn("p-0 w-[95vw] max-w-[95vw] h-[90vh]", select_bg, isFullscreen && "w-screen h-screen max-w-none")}
        className={cn(
          "flex max-h-[90vh] flex-col !gap-0 p-0",
          select_bg,
          isFullscreen ? "!h-screen !max-w-full !sm:max-w-full" : "[--dialog-max-width:56rem]",
        )}
        showCloseButton={false}
      >
        <DialogDescription className={"sr-only " + select_bg2}>Document viewer for {reference.title}</DialogDescription>
        {/* Header */}
        <DialogHeader className="flex flex-row items-center justify-between border-b bg-muted/30 px-4 py-3">
          <div className="flex items-center ">
            <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
              <FileText className="size-5 text-primary" />
            </div>
            <div className="flex flex-col">
              <DialogTitle className={"text-base font-semibold " + select_bg2}>{reference.title}</DialogTitle>
              {reference.page_number && (
                <Badge variant="outline" className="mt-1 w-fit text-xs">
                  Referenced on page {reference.page_number}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* <Button variant="ghost" size="icon" onClick={handleDownload} title="Download">
              <Download className="size-4" />
            </Button> */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} title="Close">
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        {/* Chunk Preview */}
        {reference.chunk && (
          <div className="border-b bg-yellow-50 px-4 py-3 dark:bg-yellow-950/30">
            <p className="mb-1 text-xs font-medium text-black-800 dark:text-black-200">Referenced Text</p>
            <p className="line-clamp-3 text-sm text-black-900 dark:text-black-100">{reference.chunk}</p>
          </div>
        )}

        {/* Document Viewer */}
        <ScrollArea className="flex-1 overflow-auto">
          {isPdf ? (
            <div className="flex flex-col items-center p-4 over">
              {error && (
                <div className="flex flex-col items-center gap-4 py-20">
                  <div className="flex size-16 items-center justify-center rounded-full bg-destructive/10">
                    <FileText className="size-8 text-destructive" />
                  </div>
                  <p className="text-center text-destructive">{error}</p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setError(null);
                      setIsLoading(true);
                    }}
                  >
                    Try Again
                  </Button>
                </div>
              )}
              {!error && (
                <PDFViewer
                  key={`${documentUrl}-${open}-${Math.random()}`}
                  file={documentUrl}
                  open={open}
                  currentPage={currentPage}
                  scale={scale}
                  isLoading={isLoading}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                />
              )}
            </div>
          ) : isDoc ? (
            <div className="flex flex-col items-center gap-6 p-8">
              <div className="flex size-20 items-center justify-center rounded-2xl bg-blue-100 dark:bg-blue-900/30">
                <FileText className="size-10 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-semibold">Word Document</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Word documents cannot be previewed directly in the browser.
                </p>
              </div>
              <Button onClick={handleDownload} className="gap-2">
                <Download className="size-4" />
                Download Document
              </Button>
              {reference.chunk && (
                <div className="mt-4 w-full max-w-2xl rounded-lg border bg-muted/30 p-4">
                  <p className="mb-2 text-sm font-medium">Document Extract:</p>
                  <p className="text-sm text-muted-foreground">{reference.chunk}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 p-8">
              <FileText className="size-16 text-muted-foreground" />
              <p className="text-muted-foreground">Unsupported document format</p>
            </div>
          )}
        </ScrollArea>

        {/* Footer Controls - Only for PDF */}
        {isPdf && !isLoading && !error && (
          <div className="flex flex-wrap items-center justify-between gap-4 border-t bg-muted/30 px-4 py-3">
            {/* Page Navigation */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={prevPage} disabled={currentPage <= 1}>
                <ChevronLeft className="size-4" />
              </Button>
              <div className="flex items-center gap-2">
                {/* <input type="number" className="" /> */}
                <Input
                  type="number"
                  min={1}
                  max={numPages}
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value, 10);
                    if (page >= 1 && page <= numPages) {
                      goToPage(page);
                    }
                  }}
                  className={`h-9  ${select_bg} rounded-md border bg-background px-2 text-center text-sm`}
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">of {numPages}</span>
              </div>
              <Button variant="outline" size="icon" onClick={nextPage} disabled={currentPage >= numPages}>
                <ChevronRight className="size-4" />
              </Button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={zoomOut} disabled={scale <= 0.5}>
                <ZoomOut className="size-4" />
              </Button>
              <span className="min-w-[4rem] text-center text-sm">{Math.round(scale * 100)}%</span>
              <Button variant="outline" size="icon" onClick={zoomIn} disabled={scale >= 3}>
                <ZoomIn className="size-4" />
              </Button>
            </div>

            {/* Quick Page Jump for referenced page */}
            {reference.page_number && currentPage !== reference.page_number && (
              <Button variant="secondary" size="sm" onClick={() => goToPage(reference.page_number)} className="gap-2">
                Go to page {reference.page_number}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
